import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { API, formatApiError } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AUTH } from '@/constants/testIds';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(10,37,64,0.72) 0%, rgba(10,37,64,0.88) 100%), url(https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=1600&q=80)',
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between text-white">
          <Logo variant="wordmark" className="w-full h-auto block" alt="STEELPEAK" />
          <div className="p-14 pt-0">
            <h2 className="font-display text-4xl xl:text-5xl leading-tight max-w-md">
              Reset your access.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              We'll email you a secure one-time link to choose a new password.
            </p>
            <div className="mt-10 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50">
              <div className="h-px w-8 bg-[#D4AF37]" />
              <span>STPK RAG</span>
            </div>
          </div>
        </div>
      </div>

      <div data-testid={AUTH.forgotPassword} className="relative flex items-center justify-center px-6 py-16 lg:px-16 bg-[#FAFAFA]">
        <div className="absolute top-6 right-6"><ThemeToggle /></div>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Logo variant="wordmark" className="h-7 w-auto rounded" alt="STEELPEAK" />
          </div>

          {sent ? (
            <div data-testid={AUTH.forgotDone} className="space-y-6">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-green-200 bg-green-50">
                <CheckCircle2 className="h-6 w-6 text-green-600" strokeWidth={1.5} />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-[#0A2540] leading-tight">
                Check your inbox
              </h1>
              <p className="text-sm text-slate-500 leading-relaxed">
                If an account exists for <span className="text-[#0A2540] font-medium">{email}</span>,
                we've sent a password reset link. It's valid for one hour.
              </p>
              <p className="text-xs text-slate-400">
                Didn't receive it? Check your spam folder, or try again in a minute.
              </p>
              <Link to="/login">
                <Button className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md tracking-wide">
                  Back to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                  Forgot Password
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0A2540] leading-tight">
                  Reset access
                </h1>
                <p className="mt-3 text-sm text-slate-500">
                  Enter your work email and we'll send a secure link to set a new password.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={AUTH.forgotEmail}
                    className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    placeholder="you@spwm.com"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={AUTH.forgotSubmit}
                  className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md tracking-wide"
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>

              <div className="mt-8 text-sm text-slate-500 text-center">
                Remembered it?{' '}
                <Link
                  to="/login"
                  className="text-[#0A2540] font-medium hover:text-[#D4AF37] underline underline-offset-4"
                >
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
